import type { SVGProps } from 'react';
import { cn } from '@/lib/utils';
import type { CardBrand } from '@/lib/card-formatting';
import methodCreditCardIcon from '@/assets/method-credit-card.svg';

export interface CardBrandIconProps extends Omit<SVGProps<SVGSVGElement>, 'children'> {
  brand: CardBrand | string;
  /** Pixel size; renders a fixed aspect-ratio rectangle. */
  size?: number;
}

interface PillProps {
  /** Tailwind `fill-*` token class for the pill background. */
  fillClass: string;
  label: string;
  /** Tailwind `fill-*` token class for the wordmark (defaults to white). */
  textClass?: string;
  italic?: boolean;
  weight?: number;
  fontSize?: number;
  width: number;
  height: number;
  ariaLabel: string;
  className?: string;
  rest: SVGProps<SVGSVGElement>;
}

function BrandPill({
  fillClass,
  label,
  textClass = 'fill-brand-card-light',
  italic = false,
  weight = 700,
  fontSize,
  width,
  height,
  ariaLabel,
  className,
  rest,
}: PillProps) {
  return (
    <svg
      role="img"
      aria-label={ariaLabel}
      viewBox="0 0 28 20"
      width={width}
      height={height}
      className={cn('shrink-0', className)}
      {...rest}
    >
      <rect x="0" y="0" width="28" height="20" rx="4" className={fillClass} />
      <text
        x="14"
        y="14"
        textAnchor="middle"
        fontFamily="'Outfit', sans-serif"
        fontWeight={weight}
        fontSize={fontSize ?? 8.5}
        fontStyle={italic ? 'italic' : 'normal'}
        className={textClass}
        letterSpacing="0.4"
      >
        {label}
      </text>
    </svg>
  );
}

/**
 * Small inline credit-card brand mark. Renders a recognizable wordmark on
 * a brand-coloured rounded rectangle for known brands, or a generic card
 * glyph otherwise.
 *
 * Kept as inline SVG so it doesn't require shipping a third-party logo
 * library (and so it works offline / in iframes that block external svgs).
 */
export function CardBrandIcon({ brand, size = 28, className, ...rest }: CardBrandIconProps) {
  const normalized = typeof brand === 'string' ? brand.toLowerCase() : 'unknown';
  const width = size;
  const height = Math.round(size * (20 / 28));
  const ariaLabel = `${normalized} card`;

  switch (normalized) {
    case 'visa':
      return (
        <BrandPill
          fillClass="fill-brand-visa"
          label="VISA"
          italic
          weight={800}
          width={width}
          height={height}
          ariaLabel={ariaLabel}
          className={className}
          rest={rest}
        />
      );
    case 'mastercard':
      return (
        <svg
          role="img"
          aria-label={ariaLabel}
          viewBox="0 0 28 20"
          width={width}
          height={height}
          className={cn('shrink-0', className)}
          {...rest}
        >
          <rect x="0" y="0" width="28" height="20" rx="4" className="fill-brand-card-light" />
          <rect x="0" y="0" width="28" height="20" rx="4" fill="none" stroke="rgba(0,0,0,0.08)" />
          <circle cx="11" cy="10" r="5" className="fill-brand-mc-red" />
          <circle cx="17" cy="10" r="5" className="fill-brand-mc-orange" />
          <path d="M14 6.5a5 5 0 0 1 0 7 5 5 0 0 1 0-7z" className="fill-brand-mc-overlap" />
        </svg>
      );
    case 'amex':
    case 'american express':
    case 'americanexpress':
      return (
        <BrandPill
          fillClass="fill-brand-amex"
          label="AMEX"
          weight={800}
          fontSize={7.5}
          width={width}
          height={height}
          ariaLabel={ariaLabel}
          className={className}
          rest={rest}
        />
      );
    case 'discover':
      return (
        <BrandPill
          fillClass="fill-brand-discover"
          label="DISC"
          weight={800}
          fontSize={7}
          width={width}
          height={height}
          ariaLabel={ariaLabel}
          className={className}
          rest={rest}
        />
      );
    default:
      return (
        <span
          className={cn('inline-flex items-center justify-center rounded bg-primary/[0.06] text-primary/60', className)}
          style={{ width, height }}
        >
          <img src={methodCreditCardIcon} alt="Credit Card" aria-hidden="true" className="size-14" />
        </span>
      );
  }
}
